import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";
import { NAV, getEffectiveRole } from "@/lib/site";
import { useAuth } from "@/lib/auth";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = getEffectiveRole(user);
  const links = NAV.filter((n) => !n.role || n.role === role);
  const dashPath = user && user.type === "fan" ? "/fan/dashboard" : "/dashboard";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#0a0612]/85 border-b border-white/10 py-3" : "bg-transparent py-4"
      }`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo" className="flex items-center gap-2 shrink-0">
          <div className="h-9 w-9 rounded-lg bg-brand grid place-items-center">
            <Sparkles size={18} className="text-white" />
          </div>
          <span className="font-display text-lg leading-none tracking-tight text-white">
            FAN <span className="text-brand">FOCUS</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g,'-')}`}
              className={`text-sm font-medium transition-colors ${
                pathname === n.to ? "text-white" : "text-white/60 hover:text-white"
              }`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={() => navigate(user ? dashPath : "/login")} data-testid="nav-login"
            className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium text-white/85 hover:text-white transition-colors">
            {user ? "Dashboard" : "Log In"}
          </button>
          <button onClick={() => navigate("/signup")} data-testid="nav-signup"
            className="hidden sm:inline-flex items-center bg-brand text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity shadow-[0_6px_24px_-6px_rgba(236,72,153,0.5)]">
            Sign Up
          </button>
          <button data-testid="mobile-menu-toggle" className="lg:hidden text-white" onClick={() => setOpen(v => !v)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 border-t border-white/10 bg-[#0a0612]/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4">
            {links.map(n => (
              <Link key={n.to} to={n.to} className="text-white/80 font-medium" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g,'-')}`}>{n.label}</Link>
            ))}
            <Link to={user ? dashPath : "/login"} className="text-white/80 font-medium">{user ? "Dashboard" : "Log In"}</Link>
            <Link to="/signup" className="text-brand font-semibold">Sign Up</Link>
          </div>
        </div>
      )}
    </header>
  );
};
